/*
 * The four templates the landing page's template sorter can apply to the demo page. Each one re-typesets the pitch
 * around the sign-in box (which stays a real, selected text box) and may change the page size — exactly what
 * Page Design ▸ Change Template does in the editor. Sizes are in canvas px at 96 px per inch, like the editor.
 */

export const PX_PER_IN = 96;

export const DEMO_TEMPLATES = [
  {
    id: 'flyer',
    name: 'Flyer',
    description: 'One page, a big headline and one strong picture — events, sales, notices.',
    accent: 'hsl(var(--primary))',
    art: 'flyer',
    page: { w: 816, h: 1056, margin: 48, size: '8.5 × 11 in', label: 'Letter portrait', pages: 1 },
    fonts: { display: '"Bebas Neue", "Oswald", Impact, sans-serif', body: 'Inter, system-ui, sans-serif', set: 'Bebas Neue and Inter' },
    displayScale: 1.5, displayWeight: 400, onAccent: '#fff',
    card: { x: 412, y: 150, w: 356 },
    toast: 'Letter, 8.5 × 11 in. Big condensed headline, a colour band from the master page, tear-off tabs at the foot.',
  },
  {
    id: 'menu',
    name: 'Menu',
    description: 'A centred masthead, two columns of dishes and prices with dot leaders — cafés, pop-ups, weddings.',
    accent: 'hsl(var(--wp-context-teal))',
    art: 'menu',
    page: { w: 816, h: 1056, margin: 48, size: '8.5 × 11 in', label: 'Letter portrait', pages: 1 },
    fonts: { display: '"Playfair Display", Georgia, serif', body: 'Lora, Georgia, serif', set: 'Playfair Display and Lora' },
    displayScale: 1.0, displayWeight: 500, onAccent: '#fff',
    card: { x: 412, y: 290, w: 356 },
    toast: 'Letter, 8.5 × 11 in. Double rules, dot-leader tab stops for the prices, a chef\u2019s note with a drop cap.',
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    description: 'Masthead, columns, a pull quote and page numbers — schools, clubs, teams.',
    accent: 'hsl(var(--wp-context-amber))',
    art: 'newsletter',
    page: { w: 816, h: 1056, margin: 48, size: '8.5 × 11 in', label: 'Letter portrait', pages: 4 },
    fonts: { display: '"Playfair Display", Georgia, serif', headline: '"Merriweather", "Playfair Display", Georgia, serif', body: 'Lora, Georgia, serif', set: 'Playfair Display, Merriweather and Lora' },
    displayScale: 0.95, displayWeight: 700, onAccent: 'hsl(240 6% 10%)',
    card: { x: 48, y: 320, w: 356 },
    toast: 'Letter, 8.5 × 11 in. Two linked story columns, a picture with a caption, “In this issue” with page numbers.',
  },
  {
    id: 'card',
    name: 'Card',
    description: 'Invitations and greeting cards — front and back, with fold marks.',
    accent: 'hsl(var(--wp-context-green))',
    art: 'card',
    page: { w: 576, h: 864, margin: 36, size: '6 × 9 in', label: 'Invitation', pages: 2 },
    fonts: { display: '"Great Vibes", "Dancing Script", cursive', body: 'Lora, Georgia, serif', set: 'Great Vibes and Lora' },
    displayScale: 1.5, displayWeight: 400, onAccent: '#fff',
    card: { x: 108, y: 214, w: 360 },
    toast: 'Invitation, 6 × 9 in — the page size changed with the template. Script headline, a thin frame, RSVP at the foot.',
  },
];

export const DEFAULT_TEMPLATE_ID = 'flyer';

export const templateById = (id) => DEMO_TEMPLATES.find((t) => t.id === id) || DEMO_TEMPLATES[0];

/** The sign-in box as the template lays it out: position + width in page px, natural height, unrotated. */
export const initialObject = (template) => ({ x: template.card.x, y: template.card.y, w: template.card.w, minH: 0, rot: 0 });

export const isObjectModified = (obj, template) => {
  const base = initialObject(template);
  return obj.x !== base.x || obj.y !== base.y || obj.w !== base.w || obj.minH > 0 || Math.abs(obj.rot) > 0.01;
};

/** 356 → "3.71" (inches, two decimals, trailing zeros kept so the readout does not jitter). */
export const inches = (px) => (px / PX_PER_IN).toFixed(2);

/*
 * Page Design ▸ Schemes. Hovering the Font Schemes command cycles the page through these heading / body pairings
 * (all loaded Google fonts); `displayScale` says how large a face wants to be set relative to a normal-width one, so a
 * condensed Bebas headline and a wide Montserrat one fill the same text box. Hovering Colour Schemes cycles the
 * accent — the colour band, rules and labels — with `onAccent` for text that sits on the colour.
 */
export const FONT_SCHEMES = [
  { id: 'modern', name: 'Modern', weight: 700, displayScale: 0.85, fonts: { display: 'Montserrat, Inter, sans-serif', body: 'Inter, system-ui, sans-serif', set: 'Montserrat and Inter' } },
  { id: 'editorial', name: 'Editorial', weight: 600, displayScale: 1.0, fonts: { display: '"Playfair Display", Georgia, serif', body: 'Lora, Georgia, serif', set: 'Playfair Display and Lora' } },
  { id: 'poster', name: 'Poster', weight: 400, displayScale: 1.1, fonts: { display: 'Anton, "Bebas Neue", Impact, sans-serif', body: '"Work Sans", Inter, sans-serif', set: 'Anton and Work Sans' } },
  { id: 'friendly', name: 'Friendly', weight: 700, displayScale: 0.92, fonts: { display: 'Quicksand, Nunito, sans-serif', body: 'Nunito, Inter, sans-serif', set: 'Quicksand and Nunito' } },
  { id: 'retro', name: 'Retro', weight: 400, displayScale: 0.95, fonts: { display: 'Righteous, Rubik, sans-serif', body: 'Karla, Inter, sans-serif', set: 'Righteous and Karla' } },
  { id: 'classic', name: 'Classic', weight: 700, displayScale: 0.9, fonts: { display: 'Merriweather, Georgia, serif', body: 'Lato, Inter, sans-serif', set: 'Merriweather and Lato' } },
];

export const COLOUR_SCHEMES = [
  { id: 'lagoon', name: 'Lagoon', accent: 'hsl(var(--wp-context-teal))', onAccent: '#fff' },
  { id: 'coral', name: 'Coral', accent: 'hsl(12 78% 55%)', onAccent: '#fff' },
  { id: 'marigold', name: 'Marigold', accent: 'hsl(var(--wp-context-amber))', onAccent: 'hsl(240 6% 10%)' },
  { id: 'navy', name: 'Navy', accent: 'hsl(222 47% 32%)', onAccent: '#fff' },
  { id: 'fern', name: 'Fern', accent: 'hsl(var(--wp-context-green))', onAccent: '#fff' },
  { id: 'rose', name: 'Rose', accent: 'hsl(340 65% 47%)', onAccent: '#fff' },
  { id: 'aubergine', name: 'Aubergine', accent: 'hsl(var(--primary))', onAccent: '#fff' },
];

/** Zoom, like the editor's status bar: 10 % – 400 %, two decimals. */
export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 4;
export const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((Number(z) || 1) * 100) / 100));
